import React, { useState, useMemo } from 'react'
import {
    Box,
    Container,
    Flex,
    Heading,
    Text,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
    Button,
    useDisclosure,
    Grid,
    GridItem
} from '@chakra-ui/react'
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons'
import ResponsivePicture from '../../../../components/responsive-picture'
import { FormattedMessage } from 'react-intl'

const ProductSizeGuideBlockLayout = ({
    content,
    categoryId,
    showAllCharts = true,
    showDropdown = true,
    containerProps = {},
    titleProps = {}
}) => {
    const sizeGuideTitleStylings = content?.ProductSizeGuideBlockTitleStyle;

    const filteredSizeCharts = useMemo(() => {
        const allSizeCharts = content?.SizeCharts?.map(chart =>
            chart?.ContentLink?.Expanded?.SizeChartReference?.Expanded
        ) || []

        if (!categoryId || showAllCharts) {
            return allSizeCharts
        }

        const filteredCharts = allSizeCharts.filter((chart, index) => {
            const originalChart = content?.SizeCharts?.[index]
            const categoryReference = originalChart?.ContentLink?.Expanded?.CategoryReference?.Expanded
            const chartCategoryId = categoryReference?.CategoryId

            return chartCategoryId === categoryId
        })

        return filteredCharts.length > 0 ? filteredCharts : [allSizeCharts?.[0]]
    }, [content?.SizeCharts, categoryId, showAllCharts])

    const [selectedChartIndex, setSelectedChartIndex] = useState(0)
    const [selectedUnit, setSelectedUnit] = useState('IN')
    const { isOpen, onOpen, onClose } = useDisclosure()

    const currentChart = filteredSizeCharts?.[selectedChartIndex] || {}
    const {
        BodyParts,
        MeasurementTitle,
        MeasurementTitleStyle,
        MeasurementText,
        MeasurementTextStyle,
        ResponsiveMeasurementMedia,
        SizeTables
    } = currentChart

    const convertToCentimeters = (inchValue) => {
        return (parseFloat(inchValue) * 2.54).toFixed(1)
    }

    const convertTableData = (originalTables, targetUnit) => {
        if (!originalTables || originalTables.length === 0) return []

        return originalTables.map(table => {
            if (table?.MeasurementSystem === 'IN' && targetUnit === 'CM') {
                return {
                    ...table,
                    MeasurementSystem: "CM",
                    Measurements: table?.Measurements?.map(measurement => ({
                        ...measurement,
                        Values: measurement?.Values?.map(value => ({
                            ...value,
                            MinValue: value?.MinValue ? convertToCentimeters(value?.MinValue) : value?.MinValue,
                            MaxValue: value?.MaxValue ? convertToCentimeters(value?.MaxValue) : value?.MaxValue
                        })) || []
                    })) || []
                }
            } else {
                return table
            }
        })
    }

    const currentTableData = useMemo(() => {
        return convertTableData(SizeTables, selectedUnit)
    }, [SizeTables, selectedUnit])

    const defaultFontFamily = {
        'Helvetica Neue LT Std': 'HelveticaNeueLTStd'
    }

    const defaultContainerProps = {
        margin: {base: '14px auto 80px'},
        maxW: '926px',
        paddingX: {base: 4, lg: 0},
        paddingY: 0,
        ...containerProps
    }

    const defaultTitleProps = {
        fontFamily: defaultFontFamily[sizeGuideTitleStylings?.FontFamily],
        fontSize: {
            base: sizeGuideTitleStylings?.TextSizePt,
            md: sizeGuideTitleStylings?.TextSizePtTablet,
            lg: sizeGuideTitleStylings?.TextSizePtDesktop
        },
        fontWeight: sizeGuideTitleStylings?.FontStyle,
        lineHeight: {base: '20px', md: '30px'},
        textTransform: 'uppercase',
        marginBottom: '24px',
        ...titleProps
    }

    return (
        <Container {...defaultContainerProps} data-block-type="ProductSizeGuideBlockLayout">
            {content?.Title && (
                <Heading {...defaultTitleProps}>
                    {content?.Title}
                </Heading>
            )}

            {showDropdown && filteredSizeCharts?.length > 1 && (
                <Menu
                    gutter='0'
                    matchWidth={true}
                    isOpen={isOpen}
                    onOpen={onOpen}
                    onClose={onClose}
                >
                    <MenuButton
                        as={Button}
                        backgroundColor='transparent'
                        color='black'
                        width='100%'
                        padding='12px 0'
                        fontSize='16px'
                        lineHeight='24px'
                        fontWeight='400'
                        marginBottom={{base: '70px', md: '60px'}}
                        borderBottomWidth='1px'
                        borderColor='gray.500'
                        textTransform='none'
                        aria-label={filteredSizeCharts?.[selectedChartIndex]?.ChartTitle}
                        sx={{
                            '& > span': {
                                width: '100%'
                            }
                        }}
                        _hover={{
                            backgroundColor: 'transparent'
                        }}
                        _active={{
                            backgroundColor: 'transparent'
                        }}
                    >
                        <Flex justifyContent='space-between' alignItems='center' w='100%'>
                            <Text as='span'>{filteredSizeCharts?.[selectedChartIndex]?.ChartTitle}</Text>
                            {isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                        </Flex>
                    </MenuButton>
                    <MenuList
                        borderRadius='0'
                        borderTop='0'
                        borderColor='black'
                        padding='8px 10px'
                        motionProps={{ animate: false }}
                    >
                        {filteredSizeCharts?.map((chart, index) => (
                            <MenuItem
                                key={index}
                                fontSize='14px'
                                lineHeight='18px'
                                padding='8px 4px'
                                borderBottom={index < filteredSizeCharts.length - 1 ? '1px solid black' : '0'}
                                marginTop={index !== 0 ? '8px' : '0'}
                                onClick={() => setSelectedChartIndex(index)}
                                width='100%'
                            >
                                {chart?.ChartTitle}
                            </MenuItem>
                        ))}
                    </MenuList>
                </Menu>
            )}

            <Heading
                fontSize='16px'
                lineHeight='20px'
                textTransform='uppercase'
                marginBottom={{base: '32px', md: '24px'}}
            >
                <FormattedMessage
                    id='cms.ProductSizeGuideBlockLayout.nex_up'
                    defaultMessage="Product Dimensions"
                />
            </Heading>

            <Flex>
                <Flex
                    border='0'
                    marginBottom='24px'
                    columnGap='16px'
                >
                    {['IN', 'CM'].map((unit) => (
                        <Button
                            key={unit}
                            onClick={() => setSelectedUnit(unit)}
                            variant='ghost'
                            color={selectedUnit === unit ? 'black' : 'gray.700'}
                            borderBottom={selectedUnit === unit ? '1px solid black' : '1px solid transparent'}
                            fontSize='14px'
                            lineHeight='18px'
                            padding='0'
                            borderRadius='0'
                            minWidth='auto'
                            height='auto'
                            _hover={{
                                backgroundColor: 'transparent',
                                color: 'black'
                            }}
                            _active={{
                                backgroundColor: 'transparent'
                            }}
                        >
                            {unit}
                        </Button>
                    ))}
                </Flex>
            </Flex>

            <Box
                marginBottom='24px'
            >
                {currentTableData.map(({ Measurements, MeasurementSystem, SizeColumns }) => {
                    return (
                        <Box
                            marginBottom='40px'
                            overflowX='auto'
                            marginInline={{base: '-16px', md: '0'}}
                        >
                            {!/^(IN|CM)$/i.test(MeasurementSystem) && MeasurementSystem !== '' && (
                                <Heading
                                    marginBlock='16px 0'
                                    fontSize='14px'
                                    fontWeight='500'
                                    lineHeight='17px'
                                >
                                    {MeasurementSystem}
                                </Heading>
                            )}

                            <Grid
                                templateColumns={`repeat(${SizeColumns?.length || 1}, 1fr)`}
                                gap={0}
                            >
                                {/* Header Row */}
                                {SizeColumns?.map(({ SizeLabel }, index) => (
                                    <GridItem
                                        key={index}
                                        color='black'
                                        fontSize='14px'
                                        lineHeight='16px'
                                        fontWeight='bold'
                                        borderBottom='1px solid black'
                                        paddingLeft={{md: index === 0 && 0}}
                                        textAlign={{base: 'center', md: index === 0 ? 'left' : 'center' }}
                                        whiteSpace='nowrap'
                                        padding='12px'
                                    >
                                        {SizeLabel}
                                    </GridItem>
                                ))}

                                {/* Data Rows */}
                                {Measurements?.map(({ MeasurementName, Values }, measurementIndex) => (
                                    <React.Fragment key={measurementIndex}>
                                        <GridItem
                                            fontSize='14px'
                                            lineHeight='16px'
                                            textTransform='uppercase'
                                            fontWeight='bold'
                                            borderBottom='1px solid black'
                                            paddingLeft={{md: 0}}
                                            textAlign={{base: 'center', md: 'left'}}
                                            whiteSpace='nowrap'
                                            padding='12px'
                                        >
                                            {MeasurementName}
                                        </GridItem>

                                        {/* Value cells */}
                                        {Values?.map(({ MinValue, MaxValue }, valueIndex) => (
                                            <GridItem
                                                key={valueIndex}
                                                fontSize='14px'
                                                lineHeight='18px'
                                                borderBottom='1px solid black'
                                                textAlign='center'
                                                whiteSpace='nowrap'
                                                padding='12px'
                                            >
                                                {MinValue}{MaxValue ? ` - ${MaxValue}` : ''}
                                            </GridItem>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </Grid>
                        </Box>
                    )
                })}
            </Box>

            {/* Body Measurements */}
            <Box>
                {MeasurementTitle && (
                    <Heading
                        fontSize={{
                            base: MeasurementTitleStyle?.TextSizePt,
                            md: MeasurementTitleStyle?.TextSizePtTablet,
                            lg: MeasurementTitleStyle?.TextSizePtDesktop
                        }}
                        fontFamily={{
                            base: defaultFontFamily[MeasurementTitleStyle?.FontFamily],
                            md: defaultFontFamily[MeasurementTitleStyle?.FontFamilyTablet],
                            lg: defaultFontFamily[MeasurementTitleStyle?.FontFamilyDesktop]
                        }}
                        color={{
                            base: MeasurementTitleStyle?.TextColor,
                            md: MeasurementTitleStyle?.TextColorTablet,
                            lg: MeasurementTitleStyle?.TextColorDesktop
                        }}
                        fontWeight={{
                            base: MeasurementTitleStyle?.FontStyle,
                            md: MeasurementTitleStyle?.FontStyleTablet,
                            lg: MeasurementTitleStyle?.FontStyleDesktop
                        }}
                        lineHeight='20px'
                        textTransform='uppercase'
                        marginTop='0'
                        marginBottom={{base: '16px', md: '12px'}}
                    >
                        {MeasurementTitle}
                    </Heading>
                )}

                {MeasurementText && (
                    <Text
                        color={{
                            base: MeasurementTextStyle?.TextColor,
                            md: MeasurementTextStyle?.TextColorTablet,
                            lg: MeasurementTextStyle?.TextColorDesktop
                        }}
                        fontSize={{
                            base: MeasurementTextStyle?.TextSizePt,
                            md: MeasurementTextStyle?.TextSizePtTablet,
                            lg: MeasurementTextStyle?.TextSizePtDesktop
                        }}
                        fontFamily={{
                            base: defaultFontFamily[MeasurementTextStyle?.FontFamily],
                            md: defaultFontFamily[MeasurementTextStyle?.FontFamilyTablet],
                            lg: defaultFontFamily[MeasurementTextStyle?.FontFamilyDesktop]
                        }}
                        lineHeight='17px'
                        fontWeight='bold'
                    >
                        {MeasurementText}
                    </Text>
                )}

                {ResponsiveMeasurementMedia && (
                    <ResponsivePicture
                        alt={ResponsiveMeasurementMedia?.AltText}
                        mobileSrc={ResponsiveMeasurementMedia?.Expanded?.MobileMedia?.Url}
                        tabletSrc={ResponsiveMeasurementMedia?.Expanded?.TabletMedia?.Url}
                        desktopSrc={ResponsiveMeasurementMedia?.Expanded?.DesktopMedia?.Url}
                        imageProps={{
                            width: {base: '181px', md: '283px'},
                            margin: {base: '16px auto', md: '40px auto'}
                        }}
                    />
                )}

            </Box>

            {/* Measurements Description */}
            <Flex flexDirection='column' rowGap={{base: '24px', md: '12px'}}>
                {BodyParts?.map(({ Title, Description, SizeChartsBlockTitleStyle, SizeChartsBlockDescriptionStyle }, index) => {
                    return (
                        <Box
                            key={index}
                            borderBottom={index < BodyParts.length - 1 ? '1px solid black' : 0}
                            paddingBlock={index < BodyParts.length - 1 ? '8px' : 0}
                        >
                            {Title && (
                                <Heading
                                    color={{
                                        base: SizeChartsBlockTitleStyle?.TextColor,
                                        md: SizeChartsBlockTitleStyle?.TextColorTablet,
                                        lg: SizeChartsBlockTitleStyle?.TextColorDesktop
                                    }}
                                    fontSize={{
                                        base: SizeChartsBlockTitleStyle?.TextSizePt,
                                        md: SizeChartsBlockTitleStyle?.TextSizePtTablet,
                                        lg: SizeChartsBlockTitleStyle?.TextSizePtDesktop
                                    }}
                                    fontFamily={{
                                        base: defaultFontFamily[SizeChartsBlockTitleStyle?.FontFamily],
                                        md: defaultFontFamily[SizeChartsBlockTitleStyle?.FontFamilyTablet],
                                        lg: defaultFontFamily[SizeChartsBlockTitleStyle?.FontFamilyDesktop]
                                    }}
                                    lineHeight='16px'
                                    textTransform='uppercase'
                                    margin='0 0 4px'
                                >
                                    {Title}
                                </Heading>
                            )}
                            <Text
                                color={{
                                    base: SizeChartsBlockDescriptionStyle?.TextColor,
                                    md: SizeChartsBlockDescriptionStyle?.TextColorTablet,
                                    lg: SizeChartsBlockDescriptionStyle?.TextColorDesktop
                                }}
                                fontSize={{
                                    base: SizeChartsBlockDescriptionStyle?.TextSizePt,
                                    md: SizeChartsBlockDescriptionStyle?.TextSizePtTablet,
                                    lg: SizeChartsBlockDescriptionStyle?.TextSizePtDesktop
                                }}
                                fontFamily={{
                                    base: defaultFontFamily[SizeChartsBlockDescriptionStyle?.FontFamily],
                                    md: defaultFontFamily[SizeChartsBlockDescriptionStyle?.FontFamilyTablet],
                                    lg: defaultFontFamily[SizeChartsBlockDescriptionStyle?.FontFamilyDesktop]
                                }}
                                lineHeight='18px'
                            >
                                {Description}
                            </Text>
                        </Box>
                    )
                })}
            </Flex>
        </Container>
    )
}

export default ProductSizeGuideBlockLayout
